import React, { useState, useRef, useEffect } from 'react';

export default function MobileTooltip({ children, content, className = "", side = "top", align = "center" }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        triggerRef.current && 
        !triggerRef.current.contains(e.target) &&
        contentRef.current &&
        !contentRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInteraction = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(prev => !prev);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    // Small delay before closing to allow moving to tooltip content
    timeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  if (!content) {
    return children;
  }

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const alignClasses = {
    center: '',
    start: side === 'top' || side === 'bottom' ? '-translate-x-0 left-0' : '-translate-y-0 top-0',
    end: side === 'top' || side === 'bottom' ? 'translate-x-0 right-0 left-auto' : 'translate-y-0 bottom-0 top-auto',
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={triggerRef}
        onClick={handleInteraction}
        onTouchEnd={handleInteraction}
        className="cursor-pointer touch-manipulation"
      >
        {children}
      </div>
      
      {open && (
        <div
          ref={contentRef}
          className={`absolute z-[9999] rounded-md px-3 py-1.5 text-sm shadow-lg pointer-events-auto ${positionClasses[side]} ${alignClasses[align]} ${className}`}
          style={{ maxWidth: 'min(500px, 90vw)', minWidth: '200px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      )}
    </div>
  );
}